import fs from 'node:fs'
import path from 'node:path'
import type { UninstallStep } from '@cli-types/next-adapter/commands.js'
import { CONFIG_FILE_NAME } from '@cli-constants/paths.js'
import { resolveAdminNamespace } from '@next-adapter/config/namespace/resolve-admin-namespace.js'
import { findMainCss } from '@next-adapter/main-css.js'
import { cleanCss } from '../uninstall-cleaners/clean-css.js'
import { cleanEnvFile } from '../uninstall-cleaners/clean-env-file.js'
import { cleanTsconfig } from '../uninstall-cleaners/clean-tsconfig.js'
import { isCLICreatedOxcConfig } from './is-cli-created-oxc-config.js'

export function buildUninstallPlan(cwd: string, namespaceValue: string): UninstallStep[] {
  const steps: UninstallStep[] = []
  const namespace = resolveAdminNamespace(namespaceValue)
  const hasSrc = fs.existsSync(path.join(cwd, 'src'))
  const appBase = hasSrc ? 'src/app' : 'app'

  const dirs: string[] = []
  const adminDir = path.join(cwd, namespace.segment)
  const legacyAdminDir = path.join(cwd, 'admin')
  const adminRouteGroup = path.join(cwd, appBase, namespace.routeGroup)
  const legacyAdminRouteGroup = path.join(cwd, appBase, '(admin)')
  if (fs.existsSync(adminDir)) dirs.push(`${namespace.segment}/`)
  if (namespace.segment !== 'admin' && fs.existsSync(legacyAdminDir)) dirs.push('admin/')
  if (fs.existsSync(adminRouteGroup)) dirs.push(`${appBase}/${namespace.routeGroup}/`)
  if (namespace.segment !== 'admin' && fs.existsSync(legacyAdminRouteGroup))
    dirs.push(`${appBase}/(admin)/`)

  if (dirs.length > 0) {
    steps.push({
      label: 'Admin directories',
      items: dirs,
      count: dirs.length,
      unit: dirs.length === 1 ? 'directory' : 'directories',
      execute() {
        if (fs.existsSync(adminDir)) fs.rmSync(adminDir, { recursive: true, force: true })
        if (fs.existsSync(legacyAdminDir))
          fs.rmSync(legacyAdminDir, { recursive: true, force: true })
        if (fs.existsSync(adminRouteGroup)) {
          fs.rmSync(adminRouteGroup, { recursive: true, force: true })
        }
        if (fs.existsSync(legacyAdminRouteGroup)) {
          fs.rmSync(legacyAdminRouteGroup, { recursive: true, force: true })
        }
      }
    })
  }

  const configFiles: string[] = []
  const configPaths: string[] = []
  const candidates = [
    [CONFIG_FILE_NAME, path.join(cwd, CONFIG_FILE_NAME)],
    ['drizzle.config.ts', path.join(cwd, 'drizzle.config.ts')],
    ['ADMIN.md', path.join(cwd, 'ADMIN.md')]
  ] as const

  for (const [label, fullPath] of candidates) {
    if (fs.existsSync(fullPath)) {
      configFiles.push(label)
      configPaths.push(fullPath)
    }
  }

  for (const fileName of ['.oxfmtrc.json', '.oxlintrc.json']) {
    const configPath = path.join(cwd, fileName)
    if (isCLICreatedOxcConfig(configPath)) {
      configFiles.push(`${fileName} (CLI-created)`)
      configPaths.push(configPath)
    }
  }

  if (configFiles.length > 0) {
    steps.push({
      label: 'Config files',
      items: configFiles,
      count: configFiles.length,
      unit: configFiles.length === 1 ? 'file' : 'files',
      execute() {
        for (const p of configPaths) {
          if (fs.existsSync(p)) fs.unlinkSync(p)
        }
      }
    })
  }

  const tsconfigPath = path.join(cwd, 'tsconfig.json')
  if (fs.existsSync(tsconfigPath)) {
    const content = fs.readFileSync(tsconfigPath, 'utf-8')
    const aliasMatches = [
      ...(content.match(/"@admin\//g) ?? []),
      ...(content.match(new RegExp(`"${namespace.alias}/`, 'g')) ?? [])
    ]
    if (aliasMatches && aliasMatches.length > 0) {
      const aliasCount = aliasMatches.length
      steps.push({
        label: 'tsconfig.json path aliases',
        items: [`${namespace.alias}/* aliases in tsconfig.json`],
        count: aliasCount,
        unit: aliasCount === 1 ? 'alias' : 'aliases',
        execute() {
          cleanTsconfig(tsconfigPath, namespace.alias)
        }
      })
    }
  }

  const cssFile = findMainCss(cwd)
  if (cssFile) {
    const cssContent = fs.readFileSync(cssFile, 'utf-8')
    const sourceLines = cssContent
      .split('\n')
      .filter((l) =>
        new RegExp(`^@source\\s+"[^"]*(?:admin|${namespace.segment})[^"]*";\\s*$`).test(l)
      )
    if (sourceLines.length > 0) {
      const relCss = path.relative(cwd, cssFile)
      steps.push({
        label: `CSS @source lines (${relCss})`,
        items: [`@source lines in ${relCss}`],
        count: sourceLines.length,
        unit: sourceLines.length === 1 ? 'line' : 'lines',
        execute() {
          cleanCss(cssFile, namespace.segment)
        }
      })
    }
  }

  const envPath = path.join(cwd, '.env.local')
  if (fs.existsSync(envPath)) {
    const envContent = fs.readFileSync(envPath, 'utf-8')
    const bsVars = envContent
      .split('\n')
      .filter((l) => l.trim().match(/^BETTERSTART_\w+=/))
      .map((l) => l.split('=')[0]!)
    if (bsVars.length > 0) {
      steps.push({
        label: '.env.local variables',
        items: ['BETTERSTART_* vars in .env.local'],
        count: bsVars.length,
        unit: bsVars.length === 1 ? 'variable' : 'variables',
        execute() {
          cleanEnvFile(envPath)
        }
      })
    }
  }

  return steps
}
